import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ClipboardList, Flag, Footprints, Home, MoreHorizontal } from 'lucide-react';
import { cn } from '../lib/utils';

const TERMINAL_ROUTE_PATTERNS = [
  /^\/plan\/create$/,
  /^\/races\/[^/]+$/,
  /^\/workouts\/[^/]+$/,
  /^\/plan\/workout\/[^/]+$/,
  /^\/plan\/workout\/[^/]+\/edit$/,
  /^\/workouts\/log$/,
];

export const isTerminalRoute = (pathname: string) =>
  TERMINAL_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));

const TABS = [
  {
    title: 'Today',
    path: '/dashboard',
    icon: Home,
  },
  {
    title: 'Plan',
    path: '/plan',
    icon: ClipboardList,
  },
  {
    title: 'Activities',
    path: '/workouts',
    icon: Footprints,
  },
  {
    title: 'Races',
    path: '/races',
    icon: Flag,
  },
   { title: 'More', path: '/settings', icon: MoreHorizontal },
];

const BottomTabBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(true);

  // Hide the tab bar on md and up.
  useEffect(() => {
    const update = () => setVisible(window.innerWidth < 768);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const terminalRoute = isTerminalRoute(location.pathname);

  if (!visible || terminalRoute) return null;

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-5 pb-4 md:hidden"
      style={{
        paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
      }}
      aria-label="Primary"
    >
      <div className="flex h-[4.5rem] w-full max-w-[390px] items-center justify-between rounded-[1.5rem] border border-white/70 bg-white/90 px-1.5 shadow-[0_22px_60px_rgba(13,21,39,0.20)] backdrop-blur-xl dark:border-white/10">
        {TABS.map(({ title, path, icon: Icon }) => {
          const isActive =
            location.pathname === path ||
            (path !== '/dashboard' && location.pathname.startsWith(`${path}/`));
          return (
            <button
              key={path}
              onClick={() =>
                navigate({
                  to: path,
                })
              }
              className={cn(
                'relative flex h-14 min-w-[3.2rem] shrink-0 flex-col items-center justify-center gap-0.5 rounded-2xl text-brand-slate transition-colors hover:bg-brand-slate/10 hover:text-brand-midnight',
                isActive ? 'bg-brand-slate/10 text-brand-midnight' : null,
              )}
              aria-label={title}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="h-5 w-5" strokeWidth={isActive ? 2.5 : 2.1} />
              <span className="text-[10px] font-bold leading-none">
                {title}
              </span>
              {isActive ? (
                <span
                  className="absolute bottom-1 h-1 w-6 rounded-full bg-brand-volt"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomTabBar;
